import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslationContext } from '../../../../context/TranslationContext';
import {
  ContactField_Item_Type,
  IContactFieldListItemSchema,
} from '@trii/types/dist/Contacts';
import FieldInfoLabel from '../FieldInfoLabel';

type Props = {
  nameKey: string;
  name?: string;
  value: any;
  schema: IContactFieldListItemSchema[];
  required?: boolean;
  description?: string;
  onChange: (nameKey: string, value: Record<string, any>[]) => void;
};

const buildEmptyItem = (schema: IContactFieldListItemSchema[]) =>
  schema.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {} as Record<string, any>);

const CustomPropertyListObject = ({ nameKey, name, value, schema, onChange, required, description }: Props) => {
  const t = useTranslationContext();
  const items: Record<string, any>[] = Array.isArray(value) ? value : [];
  const safeSchema = schema ?? [];
  const [newItem, setNewItem] = useState<Record<string, any>>(buildEmptyItem(safeSchema));
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Record<string, any>>({});

  const handleDelete = (index: number) => {
    onChange(nameKey, items.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    onChange(nameKey, [...items, newItem]);
    setNewItem(buildEmptyItem(safeSchema));
    setShowForm(false);
  };

  const handleCancel = () => {
    setNewItem(buildEmptyItem(safeSchema));
    setShowForm(false);
  };

  const handleEditStart = (index: number) => {
    setEditingIndex(index);
    setEditingItem({ ...items[index] });
    setShowForm(false);
  };

  const handleEditSave = () => {
    const updated = items.map((item, i) => (i === editingIndex ? editingItem : item));
    onChange(nameKey, updated);
    setEditingIndex(null);
    setEditingItem({});
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingItem({});
  };

  const renderFieldInput = (field: IContactFieldListItemSchema, isEdit: boolean = false) => {
    const currentItem = isEdit ? editingItem : newItem;
    const setCurrentItem = isEdit ? setEditingItem : setNewItem;
    if (field.type === ContactField_Item_Type.OPTIONS && field.options?.length) {
      return (
        <FormControl key={field.key} size="small" fullWidth variant="standard">
          <InputLabel shrink>{field.label}</InputLabel>
          <Select
            label={field.label}
            variant="standard"
            value={currentItem[field.key] ?? ''}
            onChange={(e) =>
              setCurrentItem((prev) => ({ ...prev, [field.key]: e.target.value }))
            }
          >
            {field.options!.map((opt: string) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    return (
      <TextField
        key={field.key}
        size="small"
        fullWidth
        label={field.label}
        variant="standard"
        required={field.required}
        InputLabelProps={{ shrink: true }}
        value={currentItem[field.key] ?? ''}
        type={
          field.type === ContactField_Item_Type.NUMBER_INTEGER ||
          field.type === ContactField_Item_Type.NUMBER_CURRENCY
            ? 'number'
            : 'text'
        }
        onChange={(e) =>
          setCurrentItem((prev) => ({ ...prev, [field.key]: e.target.value }))
        }
      />
    );
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
      >
        <FieldInfoLabel label={name ?? nameKey} description={description} />
        {required && items.length === 0 ? ' *' : ''}
      </Typography>
      {items.map((item, index) =>
        editingIndex === index ? (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              mb: 1,
              p: 1,
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
            }}
          >
            {safeSchema.map((field) => renderFieldInput(field, true))}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleEditCancel}>
                {t('global.cancel')}
              </Button>
              <Button size="small" variant="contained" onClick={handleEditSave}>
                {t('global.accept')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Card key={index} variant="outlined" sx={{ mb: 1, position: 'relative' }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, pr: 8 }}>
              <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                <IconButton size="small" onClick={() => handleEditStart(index)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {safeSchema.map((field) => (
                <Typography
                  key={field.key}
                  variant="body2"
                  sx={{ color: 'text.primary', fontSize: 12 }}
                >
                  <strong>{field.label}:</strong> {item[field.key] ?? '—'}
                </Typography>
              ))}
            </CardContent>
          </Card>
        )
      )}
      {!showForm && editingIndex === null && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(true)}
          variant="outlined"
          sx={{ alignSelf: 'end' }}
        >
          {t('global.add')}
        </Button>
      )}
      {showForm && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {safeSchema.map((field) => renderFieldInput(field, false))}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleCancel}>
              {t('global.cancel')}
            </Button>
            <Button size="small" variant="contained" onClick={handleAdd}>
              {t('global.accept')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomPropertyListObject;
